import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import Hero from '@/components/home/Hero';
import FeaturedProducts from '@/components/home/FeaturedProducts';
import Newsletter from '@/components/home/Newsletter';
import PartnersCarousel from '@/components/home/PartnersCarousel';
import { fetchFeaturedProducts } from '@/services/productService';
import { fetchShowcaseCategories } from '@/services/categoryService';
import { fetchPartners, fetchShowcaseProjects } from '@/services/partnerService';
import { useLanguage } from '@/context/LanguageContext';
import CategoryShowcaseSection from '@/components/home/CategoryShowcaseSection';
import ProjectsShowcaseSection from '@/components/home/ProjectsShowcaseSection';

const Index: React.FC = () => {
  const { tl } = useLanguage();

  // Fetch featured products
  const { data: products = [] } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: fetchFeaturedProducts,
  });

  // Fetch showcase categories
  const { data: showcaseCategories = [] } = useQuery({
    queryKey: ['showcase-categories'],
    queryFn: fetchShowcaseCategories,
  });

  // Fetch partners
  const { data: partners = [] } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchPartners,
  });

  const { data: showcaseProjects = [] } = useQuery({
    queryKey: ['showcase-projects'],
    queryFn: fetchShowcaseProjects,
  });

  const heroVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const sectionVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.9,
      y: 50,
      rotateX: 15,
      filter: "blur(5px)"
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      rotateX: 0,
      filter: "blur(0px)",
      transition: {
        duration: 1,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const newsletterVariants = {
    hidden: { 
      opacity: 0,
      y: 100,
      scale: 0.9,
      filter: "blur(5px)"
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        duration: 1,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  return (
    <AnimatePresence>
      <div>
        <Helmet>
          <title>{tl('Accueil | Meubles Karim', 'Home | Meubles Karim', 'Home | Meubles Karim')}</title>
          <meta name="description" content={tl('Meubles Karim - Meubles de luxe', 'Meubles Karim - Luxury Furniture', 'Meubles Karim - Mobili di lusso')} />
        </Helmet>

        <motion.div 
          initial="hidden"
          animate="visible"
          variants={heroVariants}
          className="relative z-10 overflow-hidden"
        >
          <Hero />
        </motion.div>
        {/* No transform wrapper — sticky left column needs a non-transformed ancestor */}
        <div id="home-content" className="relative z-20">
          <CategoryShowcaseSection 
            title={tl('Explorez Notre Collection', 'Explore Our Collection', 'Esplorate la nostra collezione')}
            subtitle={tl(
              'Parcourez une sélection soignée de meubles pour chaque espace de votre intérieur.',
              'Browse thoughtfully curated furniture for every corner of your home.',
              'Sfoglia una selezione curata di mobili per ogni angolo della tua casa.'
            )}
            categories={showcaseCategories}
          />
        </div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px" }}
          variants={sectionVariants}
          className="relative z-20"
        >
          <FeaturedProducts 
            title={tl('Collection en Tendance', 'Trending Collection', 'Collezione di tendenza')} 
            subtitle={tl(
              'Découvrez nos pièces les plus populaires, soigneusement sélectionnées pour votre maison',
              'Discover our most popular pieces, carefully selected for your home',
              'Scoprite i nostri pezzi più popolari, accuratamente selezionati per la vostra casa'
            )}
            products={products} 
          />
        </motion.div>

        <div className="relative z-20">
          <ProjectsShowcaseSection projects={showcaseProjects} />
        </div>
        
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px" }}
          variants={sectionVariants}
          className="relative z-20"
        >
          <PartnersCarousel partners={partners} />
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "0px" }}
          variants={newsletterVariants}
          className="relative z-20"
        >
          <Newsletter />
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default Index;
